import { UNIT_PAGE_SIZE_MAX, type ApiClient, type Unit, type UnitListQuery, type UnitRequest } from "@shyne/shared/api";
import type { PropertyUnit } from "@/models/properties.model";

export type UnitForm = Pick<PropertyUnit, "unit_number" | "beds" | "baths"> & { floor_plan?: string | null };

export interface UnitOption {
  id: string;
  unit_number: string;
}

export function toPropertyUnit(unit: Unit): PropertyUnit {
  return {
    id: unit.id,
    property: unit.property,
    unit_number: unit.number,
    floor_plan: unit.floorPlan ?? "",
    beds: unit.beds,
    baths: unit.baths,
  } as PropertyUnit;
}

export const toUnitOption = (unit: Unit): UnitOption => ({ id: unit.id, unit_number: unit.number });

export function toUnitRequest(form: UnitForm): UnitRequest {
  const floorPlan = form.floor_plan?.trim() ?? "";
  return { number: form.unit_number, floorPlan: floorPlan === "" ? null : floorPlan, beds: Number(form.beds), baths: Number(form.baths) };
}

type ListUnits = ApiClient["units"]["list"];

export async function everyUnit(list: ListUnits, property: string, query: Partial<UnitListQuery> = {}): Promise<Unit[]> {
  const units: Unit[] = [];
  for (let page = 1; ; page++) {
    const { items, total } = await list(property, { ...query, page, pageSize: UNIT_PAGE_SIZE_MAX });
    units.push(...items);
    if (items.length < UNIT_PAGE_SIZE_MAX || units.length >= total) return units;
  }
}

export type BatchLimits<T> = { count: number; bytes: number; sizeOf: (item: T) => number };

export function inBatches<T>(items: readonly T[], { count, bytes, sizeOf }: BatchLimits<T>): T[][] {
  const batches: T[][] = [];
  let current: T[] = [];
  let used = 0;
  for (const item of items) {
    const size = sizeOf(item);
    if (current.length > 0 && (current.length >= count || used + size > bytes)) {
      batches.push(current);
      current = [];
      used = 0;
    }
    current.push(item);
    used += size;
  }
  if (current.length > 0) batches.push(current);
  return batches;
}

export async function mapWithConcurrency<T, R>(items: readonly T[], limit: number, work: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next++;
      results[index] = await work(items[index]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}
